import { forTemplate } from 'app/common/lib/util/i18n';
import classNames from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import React, { useState } from 'react';
import RouterLink from 'app/common/components/singlespa/router-link';
import { MemberAvatarUnconnected } from 'app/src/components/MemberAvatar';
import { Noun, Verb, trackUe } from '@trello/analytics';
import styles from './TemplatesTile.less';

const format = forTemplate('templates_tile');

const getBackgroundStyle = (
  backgroundColor?: string,
  backgroundUrl?: string,
) => {
  const css: React.CSSProperties = {};
  if (backgroundUrl) {
    css.backgroundImage = `url('${backgroundUrl}')`;
    css.backgroundSize = 'cover';
    css.backgroundPosition = 'center';
  } else {
    css.backgroundColor = backgroundColor || styles.trelloBlue;
  }
  return css;
};

const getBackgroundAfterStyle = (hover: boolean) => {
  const css: React.CSSProperties = {};
  css.opacity = hover ? 1 : 0;
  return css;
};

interface TemplatesTileProps {
  avatarShape: string;
  avatarUrl: string;
  category: string;
  backgroundColor?: string;
  backgroundUrl?: string;
  blurb: string;
  boardId: string;
  byline: string;
  idOrganization: string;
  shortLink: string;
  title: string;
}

export const TemplatesTile: React.FunctionComponent<TemplatesTileProps> = ({
  avatarShape,
  avatarUrl,
  backgroundColor,
  backgroundUrl,
  blurb,
  boardId,
  byline,
  category,
  idOrganization,
  shortLink,
  title,
}) => {
  const [hover, setHover] = useState(false);
  const onHoverStart = () => {
    setHover(true);
  };
  const onHoverEnd = () => {
    setHover(false);
  };

  const onTileClick = () => {
    // TODO: update analytics with the proper values
    trackUe({
      category: Noun.TEMPLATES_GALLERY,
      verb: Verb.OPENS,
      directObj: Noun.TEMPLATES_STORY_PAGE,
      context: {
        boardId: boardId || '',
        teamId: idOrganization || '',
      },
    });
  };

  const templateSlug = `${title
    .replace(/\s+/g, '-')
    .toLowerCase()}-${shortLink}`;

  const routerLinkProps = {
    className: classNames(styles.cursor, styles.allText), // TODO: change this string interpolation to the template from b/
    href: `/templates/${category}/${templateSlug}`,
    title: title,
    onClick: onTileClick,
  };

  const templateBackgroundStyle = getBackgroundStyle(
    backgroundColor,
    backgroundUrl,
  );
  const templateBackgroundAfterStyle = getBackgroundAfterStyle(hover);

  return (
    <RouterLink {...routerLinkProps}>
      <div className={styles.templateLink}>
        <div
          className={styles.templateBackground}
          style={templateBackgroundStyle}
        />
        <div
          className={styles.templateBackgroundAfter}
          style={templateBackgroundAfterStyle}
          onMouseEnter={onHoverStart}
          onMouseLeave={onHoverEnd}
        />
        <div className={styles.templateInfo}>
          <MemberAvatarUnconnected
            className={styles.memberAvatar}
            fullName={byline}
            avatars={{
              '30': `${avatarUrl}/30.png`,
              '50': `${avatarUrl}/50.png`,
              '170': `${avatarUrl}/170.png`,
            }}
            size={48}
            avatarClassName={classNames(styles.avatar, {
              [styles.orgAvatar]: avatarShape === 'square',
            })}
          />
          <Dotdotdot clamp={2} useNativeClamp>
            <div className={styles.title} title={title}>
              {title}
            </div>
          </Dotdotdot>
          <div className={styles.author}>
            {format('by', {
              author: byline,
            })}
          </div>
          <Dotdotdot clamp={2} useNativeClamp>
            <div className={styles.blurb} title={blurb}>
              {blurb}
            </div>
          </Dotdotdot>
        </div>
      </div>
    </RouterLink>
  );
};
